import { useState, useEffect } from 'react';
import { Users, Building2, CreditCard, TrendingUp, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import type { Tenant, ManualSubscription, ManualPayment, SubscriptionPlan } from '@/types/saas';

export default function SuperAdmin() {
  const { toast } = useToast();
  const { isSuperAdmin } = useTenant();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [subscriptions, setSubscriptions] = useState<ManualSubscription[]>([]);
  const [payments, setPayments] = useState<ManualPayment[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    trialTenants: 0,
    monthlyRevenue: 0
  });

  useEffect(() => {
    if (isSuperAdmin) {
      loadData();
    }
  }, [isSuperAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load tenants
      const { data: tenantsData } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });
      
      setTenants((tenantsData || []) as Tenant[]);

      // Load subscriptions
      const { data: subsData } = await supabase
        .from('manual_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });
      
      setSubscriptions((subsData || []) as ManualSubscription[]);

      // Load payments
      const { data: paymentsData } = await supabase
        .from('manual_payments')
        .select('*')
        .order('created_at', { ascending: false });
      
      setPayments((paymentsData || []) as ManualPayment[]);

      // Load plans
      const { data: plansData } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order');
      
      setPlans((plansData || []) as SubscriptionPlan[]);

      // Calculate stats
      if (tenantsData) {
        const active = tenantsData.filter(t => t.status === 'active' && !t.is_trial);
        const trial = tenantsData.filter(t => t.is_trial);
        
        setStats({
          totalTenants: tenantsData.length,
          activeTenants: active.length,
          trialTenants: trial.length,
          monthlyRevenue: subsData?.filter(s => s.status === 'active')
            .reduce((sum, s) => sum + Number(s.amount), 0) || 0
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل البيانات',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (paymentId: string) => {
    try {
      const { error } = await supabase
        .from('manual_payments')
        .update({
          status: 'confirmed',
          confirmed_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;

      toast({
        title: 'تم التأكيد',
        description: 'تم تأكيد الدفعة بنجاح'
      });

      loadData();
    } catch (error) {
      console.error('Error approving payment:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تأكيد الدفعة',
        variant: 'destructive'
      });
    }
  };

  const handleRejectPayment = async (paymentId: string) => {
    try {
      const { error } = await supabase
        .from('manual_payments')
        .update({
          status: 'rejected',
          confirmed_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;

      toast({
        title: 'تم الرفض',
        description: 'تم رفض الدفعة'
      });

      loadData();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في رفض الدفعة',
        variant: 'destructive'
      });
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6">
          <CardContent>
            <p className="text-center text-muted-foreground">
              ليس لديك صلاحية الوصول إلى هذه الصفحة
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">لوحة تحكم المدير الرئيسي</h1>
        <p className="text-muted-foreground">إدارة جميع المستأجرين والاشتراكات</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">إجمالي المستأجرين</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalTenants}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">المستأجرين النشطين</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeTenants}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">في الفترة التجريبية</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.trialTenants}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الإيرادات الشهرية</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.monthlyRevenue.toLocaleString()} ريال</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tenants" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tenants">المستأجرين</TabsTrigger>
          <TabsTrigger value="payments">المدفوعات</TabsTrigger>
          <TabsTrigger value="subscriptions">الاشتراكات</TabsTrigger>
          <TabsTrigger value="plans">الخطط</TabsTrigger>
        </TabsList>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader>
              <CardTitle>جميع المستأجرين</CardTitle>
              <CardDescription>إدارة حسابات الشركات المسجلة</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>اسم الشركة</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>النوع</TableHead>
                    <TableHead>تاريخ الانتهاء</TableHead>
                    <TableHead>المستخدمين</TableHead>
                    <TableHead>الحجوزات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell className="font-medium">{tenant.company_name}</TableCell>
                      <TableCell>
                        <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                          {tenant.status === 'active' ? 'نشط' : tenant.status === 'suspended' ? 'معلق' : 'ملغي'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tenant.is_trial ? 'outline' : 'default'}>
                          {tenant.is_trial ? 'تجريبي' : 'مدفوع'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {tenant.is_trial 
                          ? new Date(tenant.trial_end_date).toLocaleDateString('ar-SA')
                          : tenant.subscription_end_date 
                            ? new Date(tenant.subscription_end_date).toLocaleDateString('ar-SA')
                            : '-'
                        }
                      </TableCell>
                      <TableCell>{tenant.max_users === -1 ? 'غير محدود' : tenant.max_users}</TableCell>
                      <TableCell>{tenant.max_bookings_per_month === -1 ? 'غير محدود' : tenant.max_bookings_per_month}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>المدفوعات</CardTitle>
              <CardDescription>مراجعة وتأكيد المدفوعات اليدوية</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الشركة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>طريقة الدفع</TableHead>
                    <TableHead>المرجع</TableHead>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => {
                    const tenant = tenants.find(t => t.id === payment.tenant_id);
                    return (
                      <TableRow key={payment.id}>
                        <TableCell>{tenant?.company_name || '-'}</TableCell>
                        <TableCell>{payment.amount} {payment.currency}</TableCell>
                        <TableCell>
                          {payment.payment_method === 'bank_transfer' ? 'تحويل بنكي' : 
                           payment.payment_method === 'cash' ? 'نقدي' : 'شيك'}
                        </TableCell>
                        <TableCell>{payment.reference_number || '-'}</TableCell>
                        <TableCell>{new Date(payment.payment_date).toLocaleDateString('ar-SA')}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              payment.status === 'confirmed' ? 'default' : 
                              payment.status === 'rejected' ? 'destructive' : 'secondary'
                            }
                          >
                            {payment.status === 'confirmed' ? 'مؤكد' : 
                             payment.status === 'rejected' ? 'مرفوض' : 'معلق'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {payment.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleApprovePayment(payment.id)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleRejectPayment(payment.id)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>الاشتراكات</CardTitle>
              <CardDescription>جميع الاشتراكات النشطة والمعلقة</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الشركة</TableHead>
                    <TableHead>الخطة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>تاريخ البداية</TableHead>
                    <TableHead>تاريخ الانتهاء</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => {
                    const tenant = tenants.find(t => t.id === sub.tenant_id);
                    const plan = plans.find(p => p.id === sub.plan_id);
                    return (
                      <TableRow key={sub.id}>
                        <TableCell>{tenant?.company_name || '-'}</TableCell>
                        <TableCell>{plan?.name_ar || '-'}</TableCell>
                        <TableCell>{sub.amount} {sub.currency}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              sub.status === 'active' ? 'default' : 
                              sub.status === 'expired' ? 'destructive' : 'secondary'
                            }
                          >
                            {sub.status === 'active' ? 'نشط' : 
                             sub.status === 'expired' ? 'منتهي' : 
                             sub.status === 'cancelled' ? 'ملغي' : 'معلق'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {sub.start_date ? new Date(sub.start_date).toLocaleDateString('ar-SA') : '-'}
                        </TableCell>
                        <TableCell>
                          {sub.end_date ? new Date(sub.end_date).toLocaleDateString('ar-SA') : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <CardHeader>
              <CardTitle>خطط الاشتراك</CardTitle>
              <CardDescription>إدارة خطط الاشتراك المتاحة</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الاسم</TableHead>
                    <TableHead>النوع</TableHead>
                    <TableHead>السعر</TableHead>
                    <TableHead>المستخدمين</TableHead>
                    <TableHead>الحجوزات</TableHead>
                    <TableHead>الحالة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.name_ar}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {plan.type === 'monthly' ? 'شهري' : 'سنوي'}
                        </Badge>
                      </TableCell>
                      <TableCell>{plan.price} {plan.currency}</TableCell>
                      <TableCell>{plan.max_users === -1 ? 'غير محدود' : plan.max_users}</TableCell>
                      <TableCell>{plan.max_bookings_per_month === -1 ? 'غير محدود' : plan.max_bookings_per_month}</TableCell>
                      <TableCell>
                        <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                          {plan.is_active ? 'نشط' : 'غير نشط'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}